import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "@/contexts/ToastContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Save, Shield } from "lucide-react";
import { branchesApi } from "@/lib/api/branches";
import { rolesApi } from "@/lib/api/roles";
import { PermissionMatrix } from "@/components/permissions/PermissionMatrix";
import type { PermissionGroup } from "@/lib/api/roles";

const BranchPermissionsPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { isTenantAdmin } = useAccessControl();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [permissionGroups, setPermissionGroups] = useState<PermissionGroup[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [branchName, setBranchName] = useState("");

  useEffect(() => {
    if (isTenantAdmin && id) {
      loadData();
    }
  }, [isTenantAdmin, id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [branch, catalog] = await Promise.all([
        branchesApi.getById(id!),
        rolesApi.getPermissionCatalog(),
      ]);
      setBranchName(branch.name);
      setPermissionGroups(catalog.groups);
      setSelectedPermissions(branch.permissions || []);
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Unable to load branch permissions.");
      navigate("/setup/branches");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await branchesApi.updatePermissions(id!, { permissions: selectedPermissions });
      toast.success("Branch permissions updated successfully.");
      navigate("/setup/branches");
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Unable to save permissions.");
    } finally {
      setSaving(false);
    }
  };

  if (!isTenantAdmin) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <Card className="max-w-md p-8 text-center">
            <h2 className="mb-2 text-xl font-bold text-gray-900">Access Denied</h2>
            <p className="text-gray-600">
              You need administrator permissions to manage branch permissions.
            </p>
          </Card>
        </div>
      </AppLayout>
    );
  }

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <LoadingSpinner size="xl" label="Loading permissions..." />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageContainer width="wide">
        <PageHeader
          title={`Branch Permissions: ${branchName}`}
          description="Set restrictive permissions for this branch. Staff assigned to this branch will only have permissions that match both their role and these branch permissions."
          breadcrumbItems={[
            { label: "Setup", href: "/setup" },
            { label: "Branches", href: "/setup/branches" },
            { label: branchName, href: `/setup/branches/${id}/edit` },
            { label: "Permissions" },
          ]}
          showSearch={false}
        />

        <Card className="p-6 mb-6 bg-white shadow-sm">
          <div className="flex items-start gap-3 p-4 mb-6 bg-primary/5 border border-primary/20 rounded-lg">
            <Shield className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-primary">Branch Permissions are Restrictive</p>
              <p className="text-sm text-primary mt-1">
                Staff assigned to this branch will only have permissions that exist in BOTH their
                role permissions AND these branch permissions. Leave empty to allow all role
                permissions.
              </p>
            </div>
          </div>

          <PermissionMatrix
            groups={permissionGroups}
            selectedPermissions={selectedPermissions}
            onPermissionsChange={setSelectedPermissions}
          />
        </Card>

        <div className="flex items-center justify-between pt-6 mt-6 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/setup/branches/${id}/edit`)}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            {saving ? (
              <>
                <span className="w-4 h-4 border-2 border-current rounded-full animate-spin border-t-transparent" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Permissions
              </>
            )}
          </Button>
        </div>
      </PageContainer>
    </AppLayout>
  );
};

export default BranchPermissionsPage;
